import { normalizeRoomCode } from '@orb/protocol';
import { isBoss, type SimEvent } from '@orb/sim';
import type { SimView } from './game/Driver';

/**
 * The game as a Telegram Mini App: the same page, opened from `https://t.me/<bot>/<app>`. Telegram's
 * WebApp SDK is loaded only when Telegram launched us; on the web nothing here runs.
 */

type Insets = { top: number; bottom: number; left: number; right: number };
type Haptic = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';

/** The part of `window.Telegram.WebApp` the game uses (https://core.telegram.org/bots/webapps). */
export interface WebApp {
  initData: string;
  /** Unverified copy of the launch data: fine for routing (`start_param`), never for trust. */
  initDataUnsafe?: { start_param?: string };
  version: string;
  platform: string;
  isFullscreen?: boolean;
  safeAreaInset?: Insets;
  contentSafeAreaInset?: Insets;
  isVersionAtLeast(version: string): boolean;
  ready(): void;
  expand(): void;
  requestFullscreen?(): void;
  lockOrientation?(): void;
  disableVerticalSwipes?(): void;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
  setBottomBarColor?(color: string): void;
  enableClosingConfirmation(): void;
  /** Opens a t.me link inside Telegram (the Mini App stays open since 7.0). */
  openTelegramLink?(url: string): void;
  /** Telegram's own payment sheet for an invoice link (6.1+). */
  openInvoice?(url: string, callback: (status: 'paid' | 'cancelled' | 'failed' | 'pending') => void): void;
  disableClosingConfirmation(): void;
  showConfirm(message: string, callback: (ok: boolean) => void): void;
  onEvent(event: string, handler: () => void): void;
  BackButton: { isVisible: boolean; show(): void; hide(): void; onClick(handler: () => void): void };
  HapticFeedback: { impactOccurred(style: Haptic): void; notificationOccurred(type: 'error' | 'success' | 'warning'): void; selectionChanged(): void };
}

const SDK = 'https://telegram.org/js/telegram-web-app.js?59';
const FLAG = 'orb.telegram';
/** The page's own background: Telegram's header, body and bottom bar take it, so the frame is seamless. */
export const BACKGROUND = '#060505';

/**
 * Telegram passes its launch parameters in the URL fragment (`#tgWebAppData=…`). The fragment survives
 * our own reloads (the menu navigates by URL), the session flag covers the rest.
 */
export function launchedFromTelegram(hash = location.hash): boolean {
  if (/[#&]tgWebAppData=/.test(hash)) { try { sessionStorage.setItem(FLAG, '1'); } catch { /* private mode */ } return true; }
  try { return sessionStorage.getItem(FLAG) === '1'; } catch { return false; }
}

/** Load the SDK when Telegram launched us; null on the web or when Telegram did not sign the launch. */
export async function loadTelegram(): Promise<WebApp | null> {
  if (!launchedFromTelegram()) return null;
  const w = window as Window & { Telegram?: { WebApp?: WebApp } };
  if (!w.Telegram?.WebApp) {
    const loaded = await new Promise<boolean>((resolve) => {
      const s = document.createElement('script');
      s.src = SDK; s.async = true;
      s.onload = () => resolve(true); s.onerror = () => resolve(false);
      setTimeout(() => resolve(false), 8000);
      document.head.append(s);
    });
    if (!loaded) return null;
  }
  // a link opened in an ordinary browser carries no signed data: that is the web version
  return w.Telegram?.WebApp?.initData ? w.Telegram.WebApp : null;
}

/** Device + content safe areas: what Telegram's own header and the phone's cut-outs cover. */
export function frameInsets(tg: Pick<WebApp, 'safeAreaInset' | 'contentSafeAreaInset'>): Insets {
  const a = tg.safeAreaInset, b = tg.contentSafeAreaInset;
  const side = (k: keyof Insets) => Math.max(0, Math.round((a?.[k] ?? 0) + (b?.[k] ?? 0)));
  return { top: side('top'), right: side('right'), bottom: side('bottom'), left: side('left') };
}

/** Telegram's Android client reports the device's performance class at the end of its user agent. */
export function androidPerformanceClass(ua: string): 'low' | 'average' | 'high' | null {
  const m = /Telegram-Android\/[^(]*\([^)]*;\s*(LOW|AVERAGE|HIGH)\s*\)/i.exec(ua);
  return m ? (m[1].toLowerCase() as 'low' | 'average' | 'high') : null;
}

export interface Shell {
  readonly tg: WebApp;
  /** The game is on screen: tells Telegram to drop its placeholder. */
  ready(): void;
  /** A match is on: closing asks first, the Back button offers to leave. */
  setMatch(on: boolean, leave?: () => void): void;
  /** Closes the topmost open window; false when none is open. Set by the game. */
  closeWindow: () => boolean;
  /** Feel for the player's own actions; follows the driver like any other view. */
  /** Haptics for this seat's actions; `enabled` is asked each time (the «Вібрація» setting). */
  view(seat: number, enabled?: () => boolean): SimView;
  /** Rendering may stop while Telegram has us minimised or behind another screen. */
  onActive(handler: (active: boolean) => void): void;
}

/**
 * Start the shell: full screen on phones, no swipe-to-close (dragging relics would close the app),
 * safe areas as CSS variables, Telegram's chrome in the game's colour, Back closes windows.
 */
export function startShell(tg: WebApp, root: HTMLElement = document.documentElement): Shell {
  const at = (v: string) => tg.isVersionAtLeast(v);
  const mobile = tg.platform === 'ios' || tg.platform === 'android';
  root.dataset.telegram = tg.platform;
  tg.expand();
  if (at('7.7')) tg.disableVerticalSwipes?.();
  // colours are cosmetic: a client that refuses one (hex header colours need 6.9) must not stop the game
  const paint = (f: () => void) => { try { f(); } catch { /* unsupported here */ } };
  if (at('6.9')) paint(() => tg.setHeaderColor(BACKGROUND));
  if (at('6.1')) paint(() => tg.setBackgroundColor(BACKGROUND));
  if (at('7.10')) paint(() => tg.setBottomBarColor?.(BACKGROUND));
  if (mobile && at('8.0')) {
    tg.requestFullscreen?.();
    // Telegram locks whatever the orientation is now; the game is laid out for a phone held upright
    if (innerHeight >= innerWidth) tg.lockOrientation?.();
  }

  const insets = () => {
    const f = frameInsets(tg);
    for (const k of ['top', 'right', 'bottom', 'left'] as const) root.style.setProperty(`--frame-${k}`, `${f[k]}px`);
  };
  insets();
  for (const e of ['safeAreaChanged', 'contentSafeAreaChanged', 'fullscreenChanged', 'viewportChanged']) tg.onEvent(e, insets);

  const active = new Set<(on: boolean) => void>();
  tg.onEvent('deactivated', () => active.forEach((h) => h(false)));
  tg.onEvent('activated', () => active.forEach((h) => h(true)));

  let match = false, leave: (() => void) | null = null, asking = false, closing = false;
  const confirmClosing = (on: boolean) => {
    if (on === closing || !at('6.2')) return;
    closing = on;
    if (on) tg.enableClosingConfirmation(); else tg.disableClosingConfirmation();
  };
  const shell: Shell = {
    tg,
    ready: () => tg.ready(),
    setMatch(on, exit) { match = on; leave = exit ?? null; confirmClosing(on); syncBack(); },
    closeWindow: () => false,
    view: (seat, enabled) => feel(tg, seat, () => confirmClosing(true), enabled),
    onActive: (h) => { active.add(h); },
  };

  // Back: the topmost window first; in an online match, leaving it (after asking)
  const back = () => {
    if (shell.closeWindow()) return;
    if (!match || !leave || asking) return;
    const go = leave;
    // the confirm dialog needs 6.2; an older client just leaves
    if (!at('6.2')) { go(); return; }
    asking = true;
    try { tg.showConfirm('Вийти з матчу? Поле союзника воюватиме далі.', (ok) => { asking = false; if (ok) go(); }); } catch { asking = false; go(); }
  };
  let shown = false;
  const syncBack = () => {
    if (!at('6.1')) return;
    const want = !!document.querySelector(OPEN_WINDOWS) || (match && !!leave);
    if (want === shown) return;
    shown = want;
    if (want) tg.BackButton.show(); else tg.BackButton.hide();
  };
  if (at('6.1')) {
    tg.BackButton.onClick(back);
    // windows open and close in many places; watching their `hidden` keeps the button honest
    // (attributes only: text updates in the HUD don't wake it; windows are created hidden and then shown)
    new MutationObserver(syncBack).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['hidden', 'data-open'] });
  }
  return shell;
}

/** Windows the Back button closes: the screen stack (ui/screens.ts), the mode and profile panels, the totem choice. */
export const OPEN_WINDOWS = '.ui-root[data-open], .totem-choice:not([hidden])';

/**
 * Haptics for the player's own actions — merging, summoning, a refused action — and for a boss's
 * blows. Only live ticks reach a view (a catch-up resyncs quietly), so a fast-forward never buzzes.
 * (Only bosses slam.) At most one pulse per tick, the strongest; the first own action also arms closing confirmation.
 */
export function feel(tg: Pick<WebApp, 'HapticFeedback'>, seat: number, acted: () => void = () => {}, enabled: () => boolean = () => true): SimView {
  const h = tg.HapticFeedback;
  let started = false;
  return {
    events(events: readonly SimEvent[]) {
      let pulse = 0; // the strongest this tick: 1 summon, 2 merge, 3 boss, 4 refused
      for (const e of events) {
        switch (e.type) {
          case 'summon': if (e.player === seat) pulse = Math.max(pulse, 1); break;
          case 'merged': case 'ascended': if (e.player === seat) pulse = Math.max(pulse, 2); break;
          case 'slam': pulse = Math.max(pulse, 3); break;
          case 'leak': if (isBoss(e.cls)) pulse = Math.max(pulse, 3); break;
          case 'summon_denied': case 'merge_denied': case 'skill_denied': if (e.player === seat) pulse = 4; break;
        }
        if ((e.type === 'summon' || e.type === 'merge') && e.player === seat && !started) { started = true; acted(); }
      }
      if (!enabled()) return;
      if (pulse === 1) h.impactOccurred('light');
      else if (pulse === 2) h.impactOccurred('medium');
      else if (pulse === 3) h.impactOccurred('heavy');
      else if (pulse === 4) h.notificationOccurred('error');
    },
  };
}

/* ---------- invites (T4) ---------- */

const INVITE_USED = 'orb.invite.used';

/** The room a launch link invites to (`startapp=r_<code>`), or null. */
export function invitedRoom(tg: Pick<WebApp, 'initDataUnsafe'>): string | null {
  const m = /^r_([A-Za-z0-9]{6})$/.exec(tg.initDataUnsafe?.start_param ?? '');
  return m ? normalizeRoomCode(m[1]) : null;
}

/** Whether this invite was already followed in this Telegram session (then a reload doesn't pull the player back). */
export function inviteUsed(code: string, store: Pick<Storage, 'getItem'> | null = safeSession()): boolean {
  try { return store?.getItem(INVITE_USED) === code; } catch { return false; }
}
/** Marks an invite followed — once the server has answered it (seated, or the room is gone / full). */
export function markInviteUsed(code: string, store: Pick<Storage, 'setItem'> | null = safeSession()) {
  try { store?.setItem(INVITE_USED, code); } catch { /* private mode */ }
}
function safeSession(): Storage | null { try { return sessionStorage; } catch { return null; } }

/** The link a friend taps: the Mini App with `startapp=r_<code>`, or the page itself when there is no Mini App link. */
export function inviteLink(code: string, appUrl: string | null, page?: string): string {
  if (appUrl) { const u = new URL(appUrl); u.searchParams.set('startapp', `r_${code}`); return u.href; }
  const url = new URL(page ?? location.origin + location.pathname); url.searchParams.set('room', code);
  return url.href;
}

export const INVITE_TEXT = 'Ходімо в кооп в Orb Master! Тисни посилання — і ти в моїй кімнаті.';

/**
 * Telegram's own «Share» picker with the invite (any chat or group). Uses t.me/share, so it needs no
 * inline mode on the bot and no request from our server; the Mini App stays open meanwhile.
 * False when this client can't open Telegram links (the caller copies the link instead).
 */
export function shareInvite(tg: Pick<WebApp, 'isVersionAtLeast' | 'openTelegramLink'>, link: string, text = INVITE_TEXT): boolean {
  if (!tg.openTelegramLink || !tg.isVersionAtLeast('6.1')) return false;
  tg.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`);
  return true;
}

/* ---------- Stars (T5) ---------- */

export type PurchaseResult = 'paid' | 'pending' | 'cancelled' | 'failed' | 'unsupported' | 'busy';

let buying = false;
/**
 * Buy with Telegram Stars: the server makes the invoice, Telegram's own sheet takes the payment. The
 * purchase is granted only by the server (from Telegram's payment report), so after the sheet says
 * «paid» the order is polled until the server has it — 'pending' if that takes longer than `waitMs`.
 * One purchase at a time per page; a sheet that never reports back stops waiting after `sheetMs`.
 */
export async function buyWithStars(tg: Pick<WebApp, 'isVersionAtLeast' | 'openInvoice'>, shop: {
  invoice(sku: string): Promise<{ order: number; link: string }>;
  order(id: number): Promise<{ status: string }>;
}, sku: string, o: { waitMs?: number; stepMs?: number; sheetMs?: number } = {}): Promise<PurchaseResult> {
  if (!tg.openInvoice || !tg.isVersionAtLeast('6.1')) return 'unsupported';
  if (buying) return 'busy';
  buying = true;
  try {
    const { order, link } = await shop.invoice(sku);
    const sheet = await new Promise<PurchaseResult>((resolve) => {
      const t = setTimeout(() => resolve('pending'), o.sheetMs ?? 10 * 60 * 1000);
      tg.openInvoice!(link, (status) => { clearTimeout(t); resolve(status); });
    });
    if (sheet === 'cancelled' || sheet === 'failed') return sheet;
    const until = Date.now() + (o.waitMs ?? 30000);
    while (Date.now() < until) {
      try {
        const st = (await shop.order(order)).status;
        if (st === 'paid') return 'paid';
        if (st === 'refunded' || st === 'expired') return 'failed';
      } catch { /* a blip: keep asking */ }
      await new Promise((r) => setTimeout(r, o.stepMs ?? 1000));
    }
    return 'pending';
  } finally { buying = false; }
}
